/**
 * Step 2 — Pick the ad set this ad lands under.
 *
 * Skipped when the wizard is launched with an `adSetId` prop already
 * set (e.g. from a per-ad-set "Create Ad" button — not in this PR but
 * the wizard supports it for future surfaces).
 *
 * Reads the ad sets directly from `parentCampaign.ad_sets`. No
 * additional fetch — the data is already in the campaign object the
 * page passed to the wizard.
 *
 * **Ingest-race note.** `parentCampaign.ad_sets` is sourced from the
 * saved `app_configuration`. When the user creates an ad set via
 * `CreateAdSetWizard` and the optimistic config write succeeds (the
 * common case), the new ad set appears here immediately on the next
 * cache refetch. When the parent campaign itself came from the
 * Meta-API fallback (i.e. the campaign isn't in `app_configuration`
 * yet — typically the just-linked-account first-day window), the
 * optimistic write for the ad set is intentionally skipped because the
 * walker raises `_ParentCampaignNotInConfig`. In that race the new ad
 * set won't show here until the next daily ingest reconciles. The
 * user-visible workaround is to wait for the sync or refresh after it
 * lands — the create-ad backend route's 404-on-missing parent ad set
 * already surfaces a clear error message for that case.
 */
import React from 'react';

import type { Campaign } from '@/types';

export interface AdSetPickerStepProps {
  parentCampaign: Campaign;
  selectedAdSetId: string;
  onChangeAdSetId: (adSetId: string) => void;
  errors?: {
    adSetId?: string;
  };
}

export const AdSetPickerStep: React.FC<AdSetPickerStepProps> = ({
  parentCampaign,
  selectedAdSetId,
  onChangeAdSetId,
  errors,
}) => {
  // `Campaign.ad_sets` is `MetaAdSet[]` on Meta campaigns. Same access
  // pattern as `CampaignStructure.tsx:64` (`campaign.ad_sets ?? []`).
  const rows = parentCampaign.ad_sets ?? [];
  const hasNoAdSets = rows.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Pick an ad set
        </h3>
        <p className="text-sm text-gray-600">
          Choose which ad set this new ad will live under in{' '}
          <strong>{parentCampaign.campaign_name}</strong>.
        </p>
      </div>

      {hasNoAdSets && (
        <p className="text-sm text-gray-600 italic">
          This campaign has no ad sets yet. Cancel and create an ad set
          first.
        </p>
      )}

      <fieldset
        className="space-y-2"
        aria-invalid={Boolean(errors?.adSetId)}
        aria-describedby={
          errors?.adSetId ? 'meta-ad-set-picker-error' : undefined
        }
      >
        <legend className="sr-only">Available ad sets</legend>
        {rows.map((row) => {
          const id = row.ad_set_id;
          if (!id) return null;
          return (
            <label
              key={id}
              className="flex items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="radio"
                name="meta-ad-set-picker"
                value={id}
                checked={selectedAdSetId === id}
                onChange={() => onChangeAdSetId(id)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {row.ad_set_name ?? 'Unnamed ad set'}
                </div>
                <div className="text-xs text-gray-500">
                  ID {id} · {row.status ?? 'unknown status'}
                </div>
              </div>
            </label>
          );
        })}
      </fieldset>

      {errors?.adSetId && (
        <p id="meta-ad-set-picker-error" className="text-sm text-red-600 mt-1">
          {errors.adSetId}
        </p>
      )}
    </div>
  );
};
